import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'
import './CardsSummary.css'

import { useRef } from 'react'
import { type Swiper as SwiperType } from 'swiper'
import { FreeMode, Mousewheel, Pagination } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'

import { ProjectSource } from '@/api/models/auth/lib/auth.types'
import { useFinanceBalanceMain } from '@/api/models/finance'
import { useBalances } from '@/api/models/finance/hooks/useBalances'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { CountersSummary } from '@/components/entities/counters'
import { BalanceSummary } from '@/components/entities/finance'
import { SCBalanceSummary } from '@/components/entities/finance/ui/SCBalanceSummary/SCBalanceSummary'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import { CardSkeleton } from '@/components/shared/skeleton'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { getPageRightOffset } from './utils/getPageRightOffset'
import { getResponsiveClasses } from './utils/getResponsiveClasses'

type Props = { projectSource: ProjectSource }

const CardsSummary = ({ projectSource }: Props) => {
  const {
    data: SCBalancesData,
    isLoading,
    isError: isSCBalancesError,
    error: SCBalancesError
  } = useBalances({})

  const {
    data: BUBalancesData,
    isLoading: isBalanceLoading,
    isFetching: isBalanceFetching,
    refetch: refetchBalance,
    isError: isBalanceError,
    error: balanceError
  } = useFinanceBalanceMain()

  const swiperRef = useRef<SwiperType | null>(null)
  const { breakpoint } = useCurrentBreakpoint()
  const isLargeScreen = [ScreenSize.Md, ScreenSize.Lg].includes(breakpoint)
  const isTabletScreen = [ScreenSize.Xs, ScreenSize.Sm].includes(breakpoint)
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)
  const isMobileVersion = isMobileScreen || isTabletScreen

  const isProjectBU = projectSource === ProjectSource.BU

  const SCBalancesLength = SCBalancesData?.data.length ?? 0
  const BUBalancesLength = isBalanceError
    ? 1
    : BUBalancesData?.tickets.length ?? 0

  const isSwiperVisible =
    (isMobileScreen || (isTabletScreen && SCBalancesLength !== 2)) &&
    SCBalancesLength > 1

  const isSCBalancesForbidden = isForbiddenError(SCBalancesError ?? null)

  if (isProjectBU) {
    if (isBalanceLoading && isMobileVersion) {
      return (
        <div className="mb-32 flex flex-col gap-16">
          <div className="flex gap-20 overflow-hidden">
            <CardSkeleton className="h-[184px] w-[85%] shrink-0" />
            <CardSkeleton className="h-[184px] w-[85%] shrink-0" />
          </div>
          <CardSkeleton className="mx-auto h-[8px] w-[40px]" />
          <CountersSummary
            balancesLength={BUBalancesLength}
            countersContentClassName={
              getResponsiveClasses(BUBalancesLength).countersContent
            }
          />
        </div>
      )
    }

    return (
      <div
        className={cn(
          'grid grid-cols-[repeat(auto-fit,_minmax(100px,_1fr))] grid-rows-[minmax(268px,auto)] gap-x-32',
          'lg:grid-cols-2 lg:grid-rows-[minmax(200px,auto)] lg:gap-20 lg:[&>*:nth-child(3)]:col-span-2',
          'mb-32 xl:mr-30 lg:mr-24 sm:mr-0 sm:grid-cols-1 sm:grid-rows-[auto_auto] sm:gap-16 sm:[&>*:nth-child(2)]:col-span-1',
          BUBalancesLength === 1 ? 'sm:mr-24 xs:mr-16' : ''
        )}
      >
        <BalanceSummary
          useCustomTitles
          data={BUBalancesData}
          isFetching={isBalanceFetching}
          refetch={refetchBalance}
          variant="main"
          slidesOffsetAfter={getPageRightOffset(breakpoint)}
          isError={isBalanceError}
          error={balanceError}
        />
        <div
          className={getResponsiveClasses(BUBalancesLength).countersContainer}
        >
          <CountersSummary
            balancesLength={BUBalancesLength}
            countersContentClassName={
              getResponsiveClasses(BUBalancesLength).countersContent
            }
            alignImageToBottom={BUBalancesLength === 1}
          />
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="mb-32 flex flex-col gap-20 xl:mr-30 lg:mb-24 lg:mr-24 lg:gap-12 xs:mr-16 xxs:mb-16">
        {!isMobileScreen && (
          <ul className="grid grid-cols-3 gap-20 lg:grid-cols-2 lg:gap-12">
            {Array.from({
              length: isLargeScreen || isTabletScreen ? 2 : 3
            }).map((_, index) => (
              <CardSkeleton
                key={index}
                className="h-[268px] w-full lg:h-[224px]"
              />
            ))}
          </ul>
        )}
        {isMobileScreen && (
          <div className="flex flex-col gap-12">
            <div className="flex gap-12">
              <CardSkeleton className="h-[224px] w-full" />
              <CardSkeleton className="h-[224px] w-full" />
            </div>
            <div className="flex justify-center gap-4">
              <CardSkeleton className="h-8 w-8 rounded-full" />
              <CardSkeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>
        )}
        {/**
         * Карточка приборов учёта не зависит от загрузки балансов:
         * рендерим её сразу, чтобы запрос счётчиков стартовал параллельно
         * и в этом слоте показывался только её собственный скелетон
         */}
        <CountersSummary
          balancesLength={SCBalancesLength}
          countersContentClassName={
            getResponsiveClasses(SCBalancesLength).countersContent
          }
        />
      </div>
    )
  }

  const renderSCBalances = () => {
    if (isSCBalancesForbidden) {
      return (
        <div className="col-span-full rounded-16 bg-white p-16 xxs:mr-16">
          <ErrorForbidden className="mb-[20px] mt-[20px] sm:mb-[16px] sm:mt-[16px]" />
        </div>
      )
    }

    if (isSCBalancesError) {
      return (
        <div className="col-span-full rounded-16 bg-white p-20 lg:p-16 sm:p-10 xxs:mr-16">
          <ErrorCard />
        </div>
      )
    }

    if (isSwiperVisible) {
      return (
        <SwiperComponent
          modules={[FreeMode, Mousewheel, Pagination]}
          slidesPerView="auto"
          spaceBetween={isMobileScreen ? 12 : 20}
          slidesOffsetAfter={
            isMobileScreen ? getPageRightOffset(breakpoint) : 0
          }
          mousewheel
          watchOverflow
          threshold={10}
          pagination={{ clickable: true }}
          className="h-[260px] w-full"
          onInit={(swiper: SwiperType) => (swiperRef.current = swiper)}
        >
          {SCBalancesData?.data.map(balance => (
            <SwiperSlide
              key={balance.alias}
              className="swiper-slide flex-1 sm:min-w-[220px]"
            >
              <SCBalanceSummary key={balance.alias} balance={balance} />
            </SwiperSlide>
          ))}
        </SwiperComponent>
      )
    }

    return (
      <>
        {SCBalancesData?.data.map(balance => (
          <SCBalanceSummary key={balance.alias} balance={balance} />
        ))}
      </>
    )
  }

  const isSCBalancesSectionError = isSCBalancesError || isSCBalancesForbidden

  const getSCBalancesGridClassName = () => {
    if (isSCBalancesSectionError || isSwiperVisible) {
      return 'grid-cols-1'
    }

    return getResponsiveClasses(SCBalancesLength).grid
  }

  return (
    <div className="mb-32 grid grid-cols-1 xl:mr-30  lg:mb-24 lg:mr-24 xxs:mb-16 xxs:mr-0">
      <div
        className={cn(`grid gap-20 lg:gap-12`, getSCBalancesGridClassName())}
      >
        {renderSCBalances()}

        <div
          className={cn(
            'xxs:mr-16',
            isSCBalancesSectionError || SCBalancesLength === 3
              ? 'col-span-full'
              : 'lg:col-span-full'
          )}
        >
          <CountersSummary
            balancesLength={isSCBalancesSectionError ? 0 : SCBalancesLength}
            countersContentClassName={
              getResponsiveClasses(
                isSCBalancesSectionError ? 0 : SCBalancesLength
              ).countersContent
            }
          />
        </div>
      </div>
    </div>
  )
}

export { CardsSummary }
